import { cn } from '@/lib/utils';
import { RadioGroup } from '@headlessui/react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { PatientClinicLocation } from '../contexts/PatientPortalContext';

type Props = {
  locations: PatientClinicLocation[];
  value: string | null;
  onChange: (path: string) => void;
};

const formatAddress = (loc: PatientClinicLocation) => {
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');

  return [loc.address1, cityState].filter(Boolean).join(' · ');
};

const ScheduleLocations: React.FC<Props> = ({ locations, value, onChange }) => {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      className="grid grid-cols-1 gap-y-2.5"
      aria-label="Choose a location"
    >
      {locations.map(location => {
        const path = location.path;

        if (!path) return null;

        const address = formatAddress(location);

        return (
          <RadioGroup.Option
            key={location.ID}
            value={path}
            className={({ checked, active }) =>
              cn(
                'portal-card relative flex cursor-pointer p-3.5 focus:outline-none transition-shadow',
                (checked || active) && 'ring-2 ring-[var(--primary-color)]'
              )
            }
          >
            {({ checked }) => (
              <span className="flex flex-1 items-center gap-3">
                <span
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--primary-color) 12%, transparent)',
                    color: 'var(--primary-color)',
                  }}
                >
                  <Icon icon={Icons.pinOnMap} className="h-5 w-5" />
                </span>
                <span className="flex flex-1 min-w-0 flex-col">
                  <RadioGroup.Label
                    as="span"
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
                  >
                    {location.name || address || 'Location'}
                  </RadioGroup.Label>
                  {location.name && address && (
                    <RadioGroup.Description
                      as="span"
                      className="text-xs text-gray-400 dark:text-gray-500 truncate"
                    >
                      {address}
                    </RadioGroup.Description>
                  )}
                </span>
                {checked && (
                  <Icon
                    icon={Icons.checkCircle}
                    className="h-5 w-5 shrink-0 text-[var(--primary-color)]"
                  />
                )}
              </span>
            )}
          </RadioGroup.Option>
        );
      })}
    </RadioGroup>
  );
};

export default ScheduleLocations;
